import { useState, useCallback, useEffect, useRef } from 'react';
import throttle from 'lodash/throttle';
import useEventEndHandler from './useEventEndHandler';

const scrollToEl = (htmlElement, scrollOptions = {}) => {
  const { offsetTop, ...rest } = scrollOptions;
  window.scrollTo({
    left: 0,
    top: htmlElement.offsetTop - (offsetTop ?? 0),
    behavior: 'smooth',
    ...rest
  });
};

// references must be sent in the order they appear on UI
const useScrollHandlerGroup = (elements, scrollOptionsForAll = {}) => {
  const defaultShrinkPos = 200;
  const [refActiveStatuses, setRefActiveStatuses] = useState(
    new Array(elements?.length || 0).fill(false)
  );
  const [activeRefID, setActiveRefID] = useState(null);
  const result = useRef({
    activeStatuses: refActiveStatuses,
    scrollToElement: (htmlElement, id, scrollOptions = {}) => {
      if (htmlElement) {
        setActiveRefID(id);
        scrollToEl(htmlElement, { ...scrollOptionsForAll, ...scrollOptions });
      }
    }
  });
  const calculateActiveStatuses = useCallback(
    (isScrollEnd) => {
      const targetRefID = isScrollEnd ? null : activeRefID;
      const newStatuses = elements?.reduceRight(
        (activeStatuses, element) => {
          let shrink = false;
          let distanceY = 0;
          if (!activeStatuses.union) {
            distanceY = element?.ref?.current?.getBoundingClientRect().y;
            shrink = targetRefID
              ? targetRefID === element?.id
              : (distanceY ?? window.Infinity) <
                (element?.shrinkPos || defaultShrinkPos);
          }
          return {
            union: activeStatuses.union || shrink,
            result: [shrink].concat(activeStatuses.result)
          };
        },
        { union: false, result: [] }
      ).result;
      const hasStateChanged = newStatuses.some(
        (newStatus, i) => newStatus !== refActiveStatuses[i]
      );
      if (hasStateChanged) {
        setRefActiveStatuses(newStatuses);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeRefID, elements, refActiveStatuses]
  );
  const scrollEndListener = useCallback(() => {
    if (activeRefID) {
      setActiveRefID(null);
      calculateActiveStatuses(true);
    }
  }, [activeRefID, calculateActiveStatuses]);
  const onScrollEnd = useEventEndHandler(scrollEndListener, 200);
  const onScroll = throttle(() => {
    calculateActiveStatuses();
    onScrollEnd();
  }, 100);
  useEffect(() => {
    document.addEventListener('scroll', onScroll);
    return () => {
      document.removeEventListener('scroll', onScroll);
    };
  }, [activeRefID, calculateActiveStatuses, onScroll, onScrollEnd]);

  // initial check (on load/reload)
  useEffect(() => {
    calculateActiveStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  result.current.activeStatuses = refActiveStatuses;
  return result.current;
};

export default useScrollHandlerGroup;
